import { Head, Link } from "@inertiajs/react";
import {
  Building2,
  ChevronRight,
  ExternalLink,
  Pencil,
  Users,
} from "lucide-react";
import { useState } from "react";
import BizminerAdminLayout from "@/bizminer/layouts/admin/BizminerAdminLayout.tsx";
import { CompanyLogo } from "@/components/CompanyLogo";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  bizminer_admin_organization_accounts_path,
  bizminer_admin_organization_users_path,
  bizminer_admin_organizations_path,
  bizminer_app_root_path,
} from "@/routes";
import type { BizminerAdminOrganization } from "@/types/serializers";
import CreditBalanceCard from "./_components/CreditBalanceCard";
import { EditOrganizationDialog } from "./_components/EditOrganizationDialog";
import OrganizationDetails from "./_components/OrganizationDetails";
import PageHeader from "./_components/PageHeader";
import SsoConfigCard from "./_components/SsoConfigCard";

interface Props {
  organization: BizminerAdminOrganization;
}

function UsersCard({
  organizationId,
  usersCount,
}: {
  organizationId: number;
  usersCount: number;
}) {
  return (
    <Link
      href={bizminer_admin_organization_users_path(organizationId)}
      className="block"
    >
      <Card className="hover:bg-muted/50 transition-colors cursor-pointer">
        <CardContent className="flex items-center justify-between py-4">
          <div className="flex items-center space-x-4">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Users className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h3 className="font-medium text-foreground">Users</h3>
              <p className="text-sm text-muted-foreground">
                {usersCount} user{usersCount !== 1 ? "s" : ""}
              </p>
            </div>
          </div>
          <ChevronRight className="h-5 w-5 text-muted-foreground" />
        </CardContent>
      </Card>
    </Link>
  );
}

function AccountsCard({
  organizationId,
  accountsCount,
}: {
  organizationId: number;
  accountsCount: number;
}) {
  return (
    <Link
      href={bizminer_admin_organization_accounts_path(organizationId)}
      className="block"
    >
      <Card className="hover:bg-muted/50 transition-colors cursor-pointer">
        <CardContent className="flex items-center justify-between py-4">
          <div className="flex items-center space-x-4">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Building2 className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h3 className="font-medium text-foreground">Accounts</h3>
              <p className="text-sm text-muted-foreground">
                {accountsCount} account{accountsCount !== 1 ? "s" : ""}
              </p>
            </div>
          </div>
          <ChevronRight className="h-5 w-5 text-muted-foreground" />
        </CardContent>
      </Card>
    </Link>
  );
}

export default function Show({ organization }: Props) {
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  return (
    <BizminerAdminLayout>
      <Head title={`${organization.name} - Organizations - Super Admin`} />

      <div className="min-h-screen ">
        <PageHeader
          title={organization.name!}
          backUrl={bizminer_admin_organizations_path()}
          backText="← Back to Organizations"
          icon={
            <CompanyLogo
              logoUrl={organization.logo_url}
              name={organization.name}
              className="h-8 w-8"
            />
          }
          actions={
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => setEditDialogOpen(true)}>
                <Pencil className="w-4 h-4 mr-2" />
                Edit
              </Button>
              <a
                href={bizminer_app_root_path(organization.slug)}
                className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded text-sm font-medium transition-colors inline-flex items-center"
                target="_blank"
                rel="noopener noreferrer"
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                View App
              </a>
            </div>
          }
        />

        <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
          <div className="px-4 py-6 sm:px-0 space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <OrganizationDetails organization={organization} />
              <div className="space-y-4">
                <AccountsCard
                  organizationId={organization.id}
                  accountsCount={organization.accounts_count}
                />

                <UsersCard
                  organizationId={organization.id}
                  usersCount={organization.users_count}
                />

                <CreditBalanceCard
                  organizationId={organization.id}
                  balance={organization.credits_balance}
                  spentThisMonth={organization.credits_spent_this_month}
                />
              </div>
            </div>

            <SsoConfigCard
              organizationId={organization.id}
              ssoConfig={organization.sso_config}
            />
          </div>
        </div>
      </div>

      <EditOrganizationDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        organization={organization}
      />
    </BizminerAdminLayout>
  );
}
